import type { ApiListResult } from "~/core/models/ApiListResult.interface";
import LxcError from "~/core/models/LxcError.class";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";

export interface FetchCallbacks<T, U = undefined> {
  fetchApiListResult?: (
    page: number,
    pageSize: number,
  ) => Promise<ApiListResult<T> | LxcError>;
  fetchPaginatedList?: (
    page: number,
    pageSize: number,
  ) => Promise<PaginatedList<T, U> | LxcError>;
}

/**
 * Fetch all items from a service that return PaginatedListI
 * @param callback
 */
export async function fetchAllPaginatedList<T, U = undefined>(
  callback: (
    page: number,
    pageSize: number,
  ) => Promise<PaginatedList<T, U> | LxcError>,
) {
  return await fetchAll({
    fetchPaginatedList: (page: number, pageSize: number) =>
      callback(page, pageSize),
  });
}

/**
 * Fetch all items from a service that returns ApListResult
 * @param callback
 */
export async function fetchAllApiListResult<T>(
  callback: (
    page: number,
    pageSize: number,
  ) => Promise<ApiListResult<T> | LxcError>,
): Promise<T[] | LxcError> {
  return await fetchAll({
    fetchApiListResult: (page: number, pageSize: number) =>
      callback(page, pageSize),
  });
}

async function fetchAll<T, U = undefined>({
  fetchApiListResult,
  fetchPaginatedList,
}: FetchCallbacks<T, U>) {
  let page: number = 1;
  const pageSize: number = 100;
  let numberOfItems: number = 0;
  let totalCount: number = 0;
  let doesAllItemsFetched: boolean = false;
  const itemList: T[] = [];

  while (!doesAllItemsFetched) {
    if (fetchApiListResult) {
      const response = await fetchApiListResult(page, pageSize);

      if (LxcError.check(response)) {
        doesAllItemsFetched = true;
        return response;
      }

      itemList.push(...response.data);
      numberOfItems += response.context.pageSize;
      if (page === 1) {
        totalCount = response.context.totalCount;
      }
    }
    if (fetchPaginatedList) {
      const response = await fetchPaginatedList(page, pageSize);

      if (LxcError.check(response)) {
        doesAllItemsFetched = true;
        return response;
      }

      itemList.push(...response.results);
      numberOfItems += response.pagination.pageSize;
      if (page === 1) {
        totalCount = response.pagination.totalCount;
      }
    }
    page++;
    if (numberOfItems >= totalCount) {
      doesAllItemsFetched = true;
    }
  }
  return itemList;
}
